import React, { useState, useEffect } from 'react';
import api from '../core/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ModelBreakdown {
    model: string;
    calls: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    costUSD: number;
}

interface OrgUsageRow {
    organizationId: string;
    name: string;
    slug: string;
    plan: string;
    status: string;
    isTrial: boolean;
    calls: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    avgTokensPerCall: number;
    estimatedCostUSD: number;
    topModel: string;
    models: ModelBreakdown[];
    lastActivityAt: string | null;
}

interface UsageData {
    period: string;
    orgs: OrgUsageRow[];
    totals: { calls: number; totalTokens: number; estimatedCostUSD: number };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtTokens(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
}

function timeAgo(iso: string | null): string {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    const hrs  = Math.floor(diff / 3_600_000);
    const days = Math.floor(diff / 86_400_000);
    if (mins < 1)  return 'Ahora';
    if (mins < 60) return `Hace ${mins}m`;
    if (hrs  < 24) return `Hace ${hrs}h`;
    return `Hace ${days}d`;
}

function planBadge(plan: string, isTrial: boolean) {
    if (isTrial) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    if (plan === 'pro')     return 'bg-primary/12 text-primary dark:bg-primary/30 dark:text-primary';
    if (plan === 'premium') return 'bg-primary/12 text-primary dark:bg-primary/30 dark:text-primary';
    return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400';
}

// ─── Main Component ───────────────────────────────────────────────────────────

const Monitoring: React.FC = () => {
    const [data, setData]             = useState<UsageData | null>(null);
    const [loading, setLoading]       = useState(true);
    const [period, setPeriod]         = useState<'today' | '7d' | '30d'>('30d');
    const [expanded, setExpanded]     = useState<string | null>(null);
    const [sortBy, setSortBy]         = useState<'tokens' | 'cost' | 'calls'>('tokens');

    useEffect(() => {
        setLoading(true);
        api.get(`/monitoring/ai-usage-by-org?period=${period}`)
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [period]);

    const sorted = data ? [...data.orgs].sort((a, b) => {
        if (sortBy === 'cost')  return b.estimatedCostUSD - a.estimatedCostUSD;
        if (sortBy === 'calls') return b.calls - a.calls;
        return b.totalTokens - a.totalTokens;
    }) : [];

    const maxTokens = sorted[0]?.totalTokens || 1;

    return (
        <div className="px-4 pt-6 pb-10 max-w-3xl mx-auto space-y-5">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-slate-900 dark:text-white">Consumo de IA</h1>
                    <p className="text-xs text-slate-400 mt-0.5">Tokens y costo estimado por organización</p>
                </div>
                <button
                    onClick={() => { setLoading(true); api.get(`/monitoring/ai-usage-by-org?period=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                    <span className={`material-symbols-outlined text-[18px] ${loading ? 'animate-spin' : ''}`}>refresh</span>
                </button>
            </div>

            {/* Period selector */}
            <div className="flex gap-2">
                {(['today', '7d', '30d'] as const).map(p => (
                    <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        className={`px-4 py-1.5 rounded-xl text-sm font-medium transition-colors ${
                            period === p
                                ? 'bg-primary/90 text-white shadow-sm'
                                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                        }`}
                    >
                        {p === 'today' ? 'Hoy' : p === '7d' ? '7 días' : '30 días'}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20 text-slate-300">
                    <span className="material-symbols-outlined animate-spin text-[36px]">progress_activity</span>
                </div>
            ) : !data || data.orgs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-300 dark:text-slate-600 gap-3">
                    <span className="material-symbols-outlined text-[48px]">analytics</span>
                    <p className="text-sm">Sin datos de consumo en este período</p>
                    <p className="text-xs text-slate-400">Los datos aparecen cuando las orgs usan la IA</p>
                </div>
            ) : (
                <>
                    {/* Totales globales */}
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: 'Orgs activas',    value: String(data.orgs.length),              icon: 'business',     color: 'text-primary' },
                            { label: 'Tokens totales',  value: fmtTokens(data.totals.totalTokens),    icon: 'token',        color: 'text-primary' },
                            { label: 'Costo estimado',  value: `$${data.totals.estimatedCostUSD.toFixed(4)}`, icon: 'payments', color: 'text-brand-accent' },
                        ].map(c => (
                            <div key={c.label} className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
                                <span className={`material-symbols-outlined text-[22px] ${c.color}`}>{c.icon}</span>
                                <p className="text-xl font-bold text-slate-800 dark:text-white mt-1">{c.value}</p>
                                <p className="text-[11px] text-slate-400 mt-0.5">{c.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Sort controls */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">Ordenar:</span>
                        {(['tokens', 'cost', 'calls'] as const).map(s => (
                            <button
                                key={s}
                                onClick={() => setSortBy(s)}
                                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                                    sortBy === s
                                        ? 'bg-slate-800 text-white dark:bg-white dark:text-slate-900'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                            >
                                {s === 'tokens' ? 'Tokens' : s === 'cost' ? 'Costo' : 'Llamadas'}
                            </button>
                        ))}
                    </div>

                    {/* Org list */}
                    <div className="space-y-2">
                        {sorted.map((org, i) => {
                            const isOpen = expanded === org.organizationId;
                            const pct = Math.round((org.totalTokens / maxTokens) * 100);
                            return (
                                <div
                                    key={org.organizationId}
                                    className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card overflow-hidden"
                                >
                                    {/* Row principal */}
                                    <button
                                        className="w-full text-left px-4 py-3.5 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                                        onClick={() => setExpanded(isOpen ? null : org.organizationId)}
                                    >
                                        {/* Rank */}
                                        <span className="text-sm font-bold text-slate-300 dark:text-slate-600 w-5 text-center shrink-0">
                                            {i + 1}
                                        </span>

                                        {/* Nombre y plan */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">{org.name}</p>
                                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${planBadge(org.plan, org.isTrial)}`}>
                                                    {org.isTrial ? 'trial' : org.plan}
                                                </span>
                                            </div>
                                            {/* Barra de tokens */}
                                            <div className="mt-1.5 h-1 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden w-full max-w-[200px]">
                                                <div className="h-full rounded-full bg-primary/60" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>

                                        {/* Stats compactos */}
                                        <div className="shrink-0 text-right space-y-0.5">
                                            <p className="text-sm font-bold text-slate-800 dark:text-white">{fmtTokens(org.totalTokens)}</p>
                                            <p className="text-[11px] text-brand-accent dark:text-brand-accent font-medium">${org.estimatedCostUSD.toFixed(4)}</p>
                                        </div>

                                        {/* Expand icon */}
                                        <span className={`material-symbols-outlined text-[18px] text-slate-300 dark:text-slate-600 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`}>
                                            expand_more
                                        </span>
                                    </button>

                                    {/* Detalle expandido */}
                                    {isOpen && (
                                        <div className="border-t border-slate-100 dark:border-white/5 px-4 py-3 space-y-3 bg-slate-50/50 dark:bg-slate-800/30">

                                            {/* Métricas generales */}
                                            <div className="grid grid-cols-4 gap-2">
                                                {[
                                                    { label: 'Llamadas',       value: org.calls.toLocaleString() },
                                                    { label: 'Tokens entrada', value: fmtTokens(org.promptTokens) },
                                                    { label: 'Tokens salida',  value: fmtTokens(org.completionTokens) },
                                                    { label: 'Prom/llamada',   value: fmtTokens(org.avgTokensPerCall) },
                                                ].map(m => (
                                                    <div key={m.label} className="rounded-xl bg-white dark:bg-slate-800 p-2.5 text-center border border-slate-100 dark:border-white/5">
                                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-100">{m.value}</p>
                                                        <p className="text-[10px] text-slate-400 mt-0.5">{m.label}</p>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Info extra */}
                                            <div className="flex items-center gap-4 text-xs text-slate-400">
                                                <span>
                                                    <span className="font-medium text-slate-500 dark:text-slate-300">Último uso:</span> {timeAgo(org.lastActivityAt)}
                                                </span>
                                                <span>
                                                    <span className="font-medium text-slate-500 dark:text-slate-300">Modelo top:</span> {org.topModel}
                                                </span>
                                                <span>
                                                    <span className="font-medium text-slate-500 dark:text-slate-300">Slug:</span> {org.slug}
                                                </span>
                                            </div>

                                            {/* Desglose por modelo */}
                                            {org.models.length > 0 && (
                                                <div>
                                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Desglose por modelo</p>
                                                    <div className="space-y-1">
                                                        {org.models.map(m => {
                                                            const mPct = Math.round((m.totalTokens / org.totalTokens) * 100);
                                                            return (
                                                                <div key={m.model} className="flex items-center gap-2">
                                                                    <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400 w-28 shrink-0 truncate">
                                                                        {m.model.split('/')[1] || m.model}
                                                                    </span>
                                                                    <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                                                        <div className="h-full rounded-full bg-primary/60" style={{ width: `${mPct}%` }} />
                                                                    </div>
                                                                    <span className="text-[10px] text-slate-400 w-12 text-right shrink-0">{fmtTokens(m.totalTokens)}</span>
                                                                    <span className="text-[10px] text-slate-400 w-8 text-right shrink-0">{mPct}%</span>
                                                                    <span className="text-[10px] text-brand-accent dark:text-brand-accent w-14 text-right shrink-0">${m.costUSD.toFixed(4)}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer totales */}
                    <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card px-4 py-3 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                            Total ({data.orgs.length} orgs · {data.totals.calls.toLocaleString()} llamadas)
                        </span>
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-bold text-slate-800 dark:text-white">{fmtTokens(data.totals.totalTokens)}</span>
                            <span className="text-sm font-bold text-brand-accent dark:text-brand-accent">${data.totals.estimatedCostUSD.toFixed(4)}</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Monitoring;
